import React, { useRef } from 'react';
import { CheckCircle, XCircle, Share2, ArrowRight, Lightbulb } from 'lucide-react';
import { shareToPNG } from '../utils/imageExport';

const ExplanationCard = ({ isCorrect, question, selectedAnswer, onContinue, currentScore }) => {
  const cardRef = useRef(null);

  const handleShare = async () => {
    if (cardRef.current) {
      await shareToPNG(cardRef.current, `quiz-resultado-${Date.now()}.png`);
    }
  };

  const correctAnswerText = question.options[question.correctAnswer];
  const selectedAnswerText = selectedAnswer !== null ? question.options[selectedAnswer] : '';
  const explanation = question.explanation || (isCorrect 
    ? `¡Excelente! La respuesta correcta es "${correctAnswerText}". ${question.additionalInfo || 'Continúa así para mejorar tu conocimiento de anatomía.'}`
    : `La respuesta correcta es "${correctAnswerText}". ${question.additionalInfo || 'Sigue practicando para mejorar tu conocimiento.'}`
  );

  return (
    <div 
      ref={cardRef}
      className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl border-2 ${
        isCorrect 
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300' 
          : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {isCorrect ? (
          <>
            <div className="p-3 bg-green-100 rounded-2xl">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-green-700">¡Correcto!</h3>
              <p className="text-sm text-green-600">+10 puntos</p>
            </div>
          </>
        ) : (
          <>
            <div className="p-3 bg-red-100 rounded-2xl">
              <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-red-700">Incorrecto</h3>
              <p className="text-sm text-red-600">Sigue practicando</p>
            </div>
          </>
        )}
      </div>

      {/* Question */}
      <div className="mb-4 p-4 bg-white/60 rounded-xl border border-gray-200">
        <p className="font-semibold text-gray-700 text-sm sm:text-base mb-3">
          {question.question}
        </p>
        
        {/* Selected Answer */}
        {!isCorrect && (
          <div className="mb-3 p-3 bg-red-100 rounded-lg border border-red-200">
            <p className="text-sm text-gray-600 font-medium mb-1">Tu respuesta:</p>
            <p className="text-red-700 font-semibold">{selectedAnswerText}</p>
          </div>
        )}
        
        {/* Correct Answer */}
        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
          <p className="text-sm text-gray-600 font-medium mb-1">Respuesta correcta:</p>
          <p className="text-green-700 font-semibold">{correctAnswerText}</p>
        </div>
      </div>

      {/* Explanation */}
      <div className="mb-6 p-5 bg-white/80 rounded-xl border-2 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
            <Lightbulb className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Explicación:</h4>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              {explanation}
            </p>
          </div>
        </div>
      </div>

      {/* Score Display */}
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border border-purple-200 text-center">
        <p className="text-sm text-gray-600 font-medium mb-1">Puntuación Actual</p>
        <p className="text-3xl font-bold text-purple-600">{currentScore}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onContinue}
          className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
        >
          Continuar
          <ArrowRight className="w-5 h-5" />
        </button>
        <button
          onClick={handleShare}
          className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
        >
          <Share2 className="w-5 h-5" />
          Compartir
        </button>
      </div>
    </div>
  );
};

export default ExplanationCard;
